import React, { useState, useRef, useEffect } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, Alert, ActivityIndicator, StatusBar } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AuthStackParamList } from '../../types/navigation';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import * as NavigationBar from 'expo-navigation-bar';

type Props = StackScreenProps<AuthStackParamList, 'OTPVerification'>;

const AUTH_COLORS = {
  buttonEnabled: '#C4963E',
  buttonDisabled: '#1F1F1F',
  buttonText: '#000000',
  placeholder: '#6B7280',
};

const OTPVerificationScreen: React.FC<Props> = ({ route, navigation }) => {
  const { phoneNumber } = route.params;
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const { verifyOTP, sendOTP } = useAuth();
  
  const inputRefs = useRef<(TextInput | null)[]>([]);
  const hasNavigated = useRef(false);

  // Set navigation bar on mount
  useEffect(() => {
    console.log('🎨 [OTPVerification] Setting navigation bar to black');
    if (Platform.OS === 'android') {
      NavigationBar.setBackgroundColorAsync('#000000').then(() => {
        console.log('✅ [OTPVerification] Navigation bar color set to black');
      }).catch((err) => {
        console.error('❌ [OTPVerification] Failed to set navigation bar:', err);
      });
      NavigationBar.setButtonStyleAsync('light').then(() => {
        console.log('✅ [OTPVerification] Navigation bar buttons set to light');
      }).catch((err) => {
        console.error('❌ [OTPVerification] Failed to set button style:', err);
      });
    }
  }, []);

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleOtpChange = (value: string, index: number) => {
    // Strip non-digit characters
    const digits = value.replace(/\D/g, '');

    if (!digits && value !== '') {
      return; // Non-digit, non-empty input — reject
    }

    // ── Paste / auto-fill scenario (multiple digits at once) ──
    if (digits.length > 1) {
      const newOtp = [...otp];
      const pastedDigits = digits.slice(0, 6);

      for (let i = 0; i < pastedDigits.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedDigits[i];
        }
      }

      setOtp(newOtp);

      // Move focus to the last filled box (or the 6th)
      const nextFocus = Math.min(index + pastedDigits.length, 5);
      inputRefs.current[nextFocus]?.focus();

      // Auto-submit if all digits are filled
      if (newOtp.every(d => d !== '')) {
        handleVerifyOTP(newOtp.join(''));
      }
      return;
    }

    // ── Normal single-digit typing ──
    const newOtp = [...otp];
    newOtp[index] = digits;
    setOtp(newOtp);

    // Auto-focus next input
    if (digits && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && digits && newOtp.every(d => d !== '')) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    // Handle backspace
    if (key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (otpCode?: string) => {
    const otpToVerify = otpCode || otp.join('');
    
    if (otpToVerify.length !== 6) {
      Alert.alert('Invalid OTP', 'Please enter a 6-digit OTP code.');
      return;
    }

    setLoading(true);
    try {
      console.log('Starting OTP verification...');
      const userData = await verifyOTP({ phoneNumber, otp: otpToVerify });
      console.log('OTP verification completed successfully');
      console.log('🔍 [OTPVerification] Returned user data:', userData);
      console.log('🔍 [OTPVerification] onboardingCompleted:', userData?.onboardingCompleted);
      
      // Navigate to onboarding if user hasn't completed it
      if (userData && !userData.onboardingCompleted) {
        console.log('➡️ [OTPVerification] User needs onboarding, navigating to Onboarding screen');
        hasNavigated.current = true;
        navigation.reset({
          index: 0,
          routes: [{ name: 'Onboarding' }],
        });
      } else {
        console.log('➡️ [OTPVerification] User has completed onboarding, RootNavigator will handle navigation');
        // RootNavigator will automatically show the main app since onboardingCompleted is true
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      Alert.alert('Verification Failed', error instanceof Error ? error.message : 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      setLoading(true);
      setCanResend(false);
      setResendTimer(30);
      // Clear current OTP input
      setOtp(['', '', '', '', '', '']);
      
      await sendOTP({ phoneNumber });
      Alert.alert('Success', 'OTP has been resent to your phone number.', [
        {
          text: 'OK',
          onPress: () => {
            // Focus first input after alert is dismissed
            setTimeout(() => {
              inputRefs.current[0]?.focus();
            }, 100);
          }
        }
      ]);
    } catch (error) {
      Alert.alert('Error', 'Failed to resend OTP. Please try again.');
      setCanResend(true);
      setResendTimer(0);
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = otp.some(digit => digit === '') || loading;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <StatusBar barStyle="light-content" backgroundColor="#000000" translucent={false} />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
              disabled={loading}
            >
              <ThemedText style={styles.backButtonText}>← Back</ThemedText>
            </TouchableOpacity>
          </View>

          {/* Form */}
          <View style={styles.form}>
            <ThemedText style={styles.title}>Enter OTP</ThemedText>
            <ThemedText style={styles.subtitle}>
              We've sent a 6-digit code to{'\n'}
              {phoneNumber}
            </ThemedText>

            <View style={styles.otpContainer}>
              {otp.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={ref => { inputRefs.current[index] = ref; }}
                  style={[
                    styles.otpInput,
                    { 
                      borderColor: digit !== '' ? '#C4963E' : 'rgba(255, 255, 255, 0.2)',
                    },
                  ]}
                  value={digit}
                  onChangeText={value => handleOtpChange(value, index)}
                  onKeyPress={({ nativeEvent: { key } }) => handleKeyPress(key, index)}
                  keyboardType="number-pad"
                  maxLength={6}
                  autoFocus={index === 0}
                  editable={!loading}
                  selectTextOnFocus
                  textContentType="oneTimeCode"
                  autoComplete="sms-otp"
                  placeholderTextColor={AUTH_COLORS.placeholder}
                />
              ))}
            </View>

            <TouchableOpacity
              style={[styles.button, { backgroundColor: isButtonDisabled ? AUTH_COLORS.buttonDisabled : AUTH_COLORS.buttonEnabled }]}
              onPress={() => handleVerifyOTP()}
              disabled={isButtonDisabled}
            >
              {loading ? (
                <ActivityIndicator color={AUTH_COLORS.buttonText} />
              ) : (
                <ThemedText style={[styles.buttonText, { color: AUTH_COLORS.buttonText }]}>Verify OTP</ThemedText>
              )}
            </TouchableOpacity>

            {/* Resend OTP */}
            <View style={styles.resendContainer}>
              {canResend ? (
                <TouchableOpacity onPress={handleResendOTP}>
                  <ThemedText style={styles.resendText}>Resend OTP</ThemedText>
                </TouchableOpacity>
              ) : (
                <ThemedText style={styles.timerText}>
                  Resend OTP in {resendTimer}s
                </ThemedText>
              )}
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  keyboardView: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      maxWidth: 480,
      width: '100%',
      marginHorizontal: 'auto' as any,
    } : {}),
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
  },
  header: {
    marginTop: 16,
  },
  backButton: {
    paddingVertical: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#C4963E',
  },
  form: {
    flex: 1,
    justifyContent: 'center',
    marginTop: -80,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 40,
    lineHeight: 24,
    color: '#9CA3AF',
  },
  otpContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 32,
    gap: 8,
  },
  otpInput: {
    width: 48,
    height: 56,
    borderWidth: 2,
    borderRadius: 12,
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    includeFontPadding: false,
    textAlignVertical: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: '#FFFFFF',
  },
  button: {
    height: 56,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  resendContainer: {
    alignItems: 'center',
  },
  resendText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#C4963E',
  },
  timerText: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  footer: {
    paddingBottom: 24,
  },
  hintText: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
    color: '#6B7280',
  },
});

export default OTPVerificationScreen;
